import { useState } from 'react'
import { toast } from 'react-toastify'
import {
  useEnvelopeAction,
  useEnvelopeGroups,
  useEnvelopes,
  useEnvelopesOverview,
  useIncidents,
  useServiceOverview,
} from '@/hooks/useDeepDive'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useCursorPages } from '@/hooks/useCursorPages'
import {
  cooldownUntil,
  toEnvelopes,
  toStatusBuckets,
  statusTotal,
  toRequestSources,
} from '@/lib/docusign-data'
import {
  coverageNote,
  dataSourceState,
  naNumber,
  naPercent,
  toFreshness,
  toHealthTiles,
  toIncidents,
} from '@/lib/deep-dive-adapters'
import type { EnvelopeGroupBy, SalesPeriodSelection } from '@/lib/api/deep-dive.types'
import type { Envelope } from '@/lib/docusign-data'
import { ServiceOutageHeader } from '@/components/systems/ServiceOutageHeader'
import { EnvelopeStatusPanel } from '@/components/systems/EnvelopeStatusPanel'
import { RequestSourceDonut } from '@/components/systems/RequestSourceDonut'
import { ENVELOPE_PAGE_SIZES, EnvelopeQueueTable } from '@/components/systems/EnvelopeQueueTable'
import { IncidentHistoryTable } from '@/components/systems/IncidentHistoryTable'
import { StatTile } from '@/components/systems/StatTile'
import { PeriodPicker } from '@/components/systems/PeriodPicker'
import { PanelError, PanelLoading } from '@/components/systems/PanelState'

/** Envelope rows per page on first load. */
const DEFAULT_ENVELOPE_PAGE_SIZE = ENVELOPE_PAGE_SIZES[0]

/**
 * DocuSign's service screen — the signature step, so it reads as a queue rather
 * than as telemetry.
 *
 * Two independent feeds: health (`docusign/overview`, `docusign/incidents`) and
 * envelopes (`docusign/envelopes*`). They are kept in separate bands, each panel
 * owning its own failure state, because the envelope half only began answering
 * on 2026-09-08 — before that it returned 502 for weeks while health worked
 * fine. One page-wide error state would have blanked working uptime figures for
 * the whole of that period, and will again the next time one side is down.
 *
 * The provider is confirmed as DocuSign (`provider: "docusign"` on the overview
 * payload) rather than the BoldSign the contract left open, but nothing below
 * depends on that — the panels read the vendor-neutral fields.
 *
 * Note the envelope counts span the **whole shared DocuSign account**, staging
 * included; the payload's `notes` say so and they are surfaced in the status
 * panel's tooltip rather than left in the network tab.
 */
export function DocuSignManagement() {
  /**
   * The period every envelope figure on this screen covers.
   *
   * Replaces the 7d/30d/90d switch: the envelope routes now take the same
   * `period` the sales screens do, so DocuSign gets the same nine options and
   * the same custom range instead of three fixed spans. `this_month` rather
   * than `today` as the default, because a signature queue read a day at a
   * time is mostly empty — the old default was the 30-day window.
   */
  const [period, setPeriod] = useState<SalesPeriodSelection>({ key: 'this_month' })
  const [groupBy, setGroupBy] = useState<EnvelopeGroupBy>('office')
  const [search, setSearch] = useState('')
  // The search box reaches the API, so it waits for a pause in typing rather
  // than firing a request per keystroke.
  const debouncedSearch = useDebouncedValue(search, 350)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_ENVELOPE_PAGE_SIZE)
  const pages = useCursorPages()
  /** Row id → when its action becomes available again, learned from a 409. */
  const [cooldowns, setCooldowns] = useState<Record<string, Date>>({})
  const [pendingId, setPendingId] = useState<string | null>(null)

  const health = useServiceOverview('docusign')
  const incidents = useIncidents('docusign')
  const overview = useEnvelopesOverview(period)
  const groups = useEnvelopeGroups(groupBy, period)
  // The queue takes the period too. It did not take the old `window` — that
  // parameter only existed on the two summary routes — so before this the table
  // below could list an envelope that none of the figures above counted.
  const envelopes = useEnvelopes({
    period,
    search: debouncedSearch || undefined,
    limit: pageSize,
    cursor: pages.cursor,
  })
  /** Said once, under the band heading, when the sync starts inside the period. */
  const coverage = coverageNote(overview.data?.coverage ?? groups.data?.coverage)
  const action = useEnvelopeAction()

  const openIncident = health.data?.open_incident ?? null

  const chase = (envelope: Envelope) => {
    if (envelope.action === 'none') return
    setPendingId(envelope.id)
    action.mutate(
      {
        envelopeId: envelope.envelopeId,
        recipientId: envelope.recipientId,
        action: envelope.action,
      },
      {
        onSettled: () => setPendingId(null),
        onSuccess: (result) => {
          toast.success(result.message || `${envelope.envelopeId}: ${result.action} sent.`)
          // A successful action can also carry its own cooldown, which is what
          // stops someone sending the same reminder twice in ten seconds.
          if (result.cooldown_until) {
            setCooldowns((c) => ({ ...c, [envelope.id]: new Date(result.cooldown_until!) }))
          }
        },
        onError: (error) => {
          const until = cooldownUntil(error)
          if (until) setCooldowns((c) => ({ ...c, [envelope.id]: until }))
          toast.error(dataSourceState(error).detail ?? 'That action was rejected.')
        },
      },
    )
  }

  return (
    <div className="space-y-6 pb-4">
      {openIncident ? (
        <ServiceOutageHeader
          title="DocuSign Management"
          downSince={new Date(openIncident.started_at)}
          note={`${openIncident.indicator} · ${openIncident.to_status.replace('_', ' ')}`}
        />
      ) : (
        <h1 className="text-fg text-3xl leading-tight font-semibold tracking-tight sm:text-[32px]">
          DocuSign Management
        </h1>
      )}

      {/* Health first, and separately: it is the half of this screen that has a
          live feed behind it today. */}
      {health.isError ? (
        <PanelError
          error={health.error}
          onRetry={() => void health.refetch()}
          className="border-line bg-surface rounded-lg border"
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {toHealthTiles(health.data, incidents.data?.total).map((stat) => (
            <StatTile key={stat.id} stat={stat} />
          ))}
        </div>
      )}

      {/* The period heads this band rather than the page, because the band is
          what it governs: every envelope route takes it, and the queue below
          reads off the same one. Health above and the incident log at the foot
          ignore it — which is why it is not beside the page title, directly over
          a "30-Day Uptime" tile that is 30 days whatever this says. */}
      <div className="border-line flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b pb-3">
        <div className="min-w-0">
          <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
            Envelope activity
          </h2>
          {overview.data?.period && (
            <p className="text-fg-subtle mt-1 font-mono text-[10px] tracking-[0.06em] uppercase">
              {overview.data.period.from_date} → {overview.data.period.to_date}
              {overview.data.period.in_progress ? ' · in progress' : ''}
              {coverage ? ` · ${coverage}` : ''}
            </p>
          )}
        </div>
        <PeriodPicker
          value={period}
          onChange={(next) => {
            setPeriod(next)
            // A cursor encodes a position under the old period; keeping it would
            // answer page 2 with rows from a span the screen has left.
            pages.reset()
          }}
        />
      </div>

      {/* Four columns, not three, with the office breakdown taking two of them.
          The three panels do not want equal width: the two stat cards hold one
          number each, the status bars hold six short rows, and the breakdown
          holds a donut plus four office names long enough to wrap. Splitting the
          row evenly starved the one panel with real content to lay out. */}
      <div className="grid gap-4 xl:grid-cols-4">
        {/* The two small cards stack into one column beside the wider panels. */}
        <div className="grid gap-4">
          <section className="border-line bg-surface rounded-lg border p-5">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
                Pending Signatures
              </h2>
              {/* Amber, because a pending pile is a warning, not a failure. */}
              <span
                title="Envelopes waiting on a signer"
                className="bg-degraded mt-0.5 size-2.5 shrink-0 rounded-full"
              />
            </div>
            {overview.isLoading ? (
              <PanelLoading />
            ) : overview.isError ? (
              <PanelError error={overview.error} onRetry={() => void overview.refetch()} />
            ) : (
              <p className="mt-4 flex items-baseline gap-2">
                <span className="text-fg font-mono text-4xl leading-none font-bold">
                  {naNumber(overview.data?.pending_signatures)}
                </span>
                <span className="text-fg-muted text-base">Docs</span>
              </p>
            )}
          </section>

          <section className="border-line bg-surface rounded-lg border p-5">
            <h2 className="text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase">
              Sent to Signed Ratio
            </h2>
            {overview.isLoading ? (
              <PanelLoading />
            ) : overview.isError ? (
              <PanelError error={overview.error} onRetry={() => void overview.refetch()} />
            ) : (
              <div className="mt-4 flex items-center gap-4">
                <span className="text-primary-bright font-mono text-4xl leading-none font-bold">
                  {naPercent(overview.data?.sent_to_signed_pct)}
                </span>
                {/* No meter when there is no ratio: a bar drawn at zero would
                    read as "nothing came back signed", which is not what a null
                    means — it means nothing was sent. */}
                {typeof overview.data?.sent_to_signed_pct === 'number' && (
                  <div
                    role="meter"
                    aria-valuenow={overview.data.sent_to_signed_pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Sent to signed ratio"
                    className="bg-surface-3 h-2.5 min-w-0 flex-1 rounded-full"
                  >
                    <div
                      className="bg-primary-bright h-full rounded-full"
                      style={{ width: `${overview.data.sent_to_signed_pct}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        <EnvelopeStatusPanel
          buckets={toStatusBuckets(overview.data)}
          total={statusTotal(overview.data)}
          notes={overview.data?.notes}
          isLoading={overview.isLoading}
          error={overview.error}
          onRetry={() => void overview.refetch()}
        />

        <div className="xl:col-span-2">
          <RequestSourceDonut
            sources={toRequestSources(groups.data)}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            isLoading={groups.isLoading}
            error={groups.error}
            onRetry={() => void groups.refetch()}
          />
        </div>
      </div>

      <EnvelopeQueueTable
        envelopes={toEnvelopes(envelopes.data)}
        query={search}
        onQueryChange={(next) => {
          setSearch(next)
          // A cursor is a position inside one query's result set — carrying it
          // into a different search would answer with rows from the old one.
          pages.reset()
        }}
        onAction={chase}
        pendingId={pendingId}
        cooldowns={cooldowns}
        freshness={envelopes.data && toFreshness(envelopes.data)}
        isLoading={envelopes.isLoading}
        isFetching={envelopes.isFetching}
        error={envelopes.error}
        onRetry={() => void envelopes.refetch()}
        pager={{
          page: pages.page,
          pageSize,
          // The feed says whether another page exists; it never says how many.
          hasNext: Boolean(envelopes.data?.next_cursor),
          onPrev: pages.prev,
          onNext: () => pages.next(envelopes.data?.next_cursor),
          onPageSizeChange: (size) => {
            setPageSize(size)
            // Cursors encode a position at a given page size, so every one held
            // becomes meaningless the moment the size changes.
            pages.reset()
          },
        }}
      />

      <IncidentHistoryTable
        incidents={toIncidents(incidents.data?.incidents)}
        total={incidents.data?.total}
        isLoading={incidents.isLoading}
        error={incidents.error}
        onRetry={() => void incidents.refetch()}
      />
    </div>
  )
}
